<template>
  <div class="communities-page">
    <div class="filter-bar">
      <div class="search-box">
        <input 
          type="text" 
          v-model="searchKey" 
          placeholder="搜索小区名称..."
          class="search-input"
        />
        <button @click="search" class="search-btn">搜索</button>
      </div>
      <button @click="showAddModal = true" class="add-btn">+ 添加小区</button>
    </div>

    <div class="table-container">
      <table class="communities-table">
        <thead>
          <tr>
            <th>小区ID</th>
            <th>小区名称</th>
            <th>跑手</th>
            <th>店家</th>
            <th>所在省市区</th>
            <th>地址</th>
            <th>楼栋数</th>
            <th>天气类型</th>
            <th>状态</th>
            <th>创建时间</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="community in communities" :key="community.id">
            <td>{{ community.id }}</td>
            <td>{{ community.name }}</td>
            <td>{{ community.runner_count || 0 }}</td>
            <td>{{ community.shop_count || 0 }}</td>
            <td>{{ community.provinceName || '' }} {{ community.cityName || '' }} {{ community.districtName || '' }}</td>
            <td>{{ community.address }}</td>
            <td>{{ community.building_count || 0 }}</td>
            <td>
              <span class="weather-type-tag">
                {{ getWeatherTypeName(community.weatherTypeId || community.weather_type_id) }}
              </span>
            </td>
            <td><span :class="'status status-' + community.status">{{ community.status === 1 ? '启用' : '禁用' }}</span></td>
            <td>{{ community.created_at?.substring(0, 10) }}</td>
            <td>
              <button @click="viewDetail(community)" class="action-btn view">查看</button>
              <button @click="viewBuildings(community)" class="action-btn buildings">楼栋</button>
              <button @click="editCommunity(community)" class="action-btn edit">编辑</button>
              <button @click="toggleStatus(community)" class="action-btn" :class="community.status === 1 ? 'disable' : 'enable'">
                {{ community.status === 1 ? '禁用' : '启用' }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="pagination">
      <button :disabled="currentPage === 1" @click="currentPage--" class="page-btn">上一页</button>
      <span class="page-info">第 {{ currentPage }} / {{ totalPages }} 页</span>
      <button :disabled="currentPage === totalPages" @click="currentPage++" class="page-btn">下一页</button>
    </div>

    <div v-if="showAddModal" class="modal-overlay" @click.self="closeModal">
      <div class="modal-content">
        <div class="modal-header">
          <h3>{{ editingCommunity ? '编辑小区' : '添加小区' }}</h3>
          <button @click="closeModal" class="close-btn">×</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>小区名称</label>
            <input v-model="form.name" type="text" class="form-control" placeholder="请输入小区名称" />
          </div>
          <div class="form-group">
            <label>所在地区</label>
            <div class="region-select">
              <select v-model="form.provinceId" @change="handleProvinceChange" class="region-select-item">
                <option value="">请选择省</option>
                <option v-for="province in provinceList" :key="province.id" :value="province.id">
                  {{ province.name }}
                </option>
              </select>
              <select v-model="form.cityId" @change="handleCityChange" class="region-select-item" :disabled="!form.provinceId">
                <option value="">请选择市</option>
                <option v-for="city in cityList" :key="city.id" :value="city.id">
                  {{ city.name }}
                </option>
              </select>
              <select v-model="form.districtId" class="region-select-item" :disabled="!form.cityId">
                <option value="">请选择区县</option>
                <option v-for="district in districtList" :key="district.id" :value="district.id">
                  {{ district.name }}
                </option>
              </select>
            </div>
          </div>
          <div class="form-group">
            <label>详细地址</label>
            <input v-model="form.address" type="text" class="form-control" placeholder="请输入详细地址" />
          </div>
          <div class="form-group">
            <label>楼栋数</label>
            <input v-model.number="form.buildingCount" type="number" class="form-control" placeholder="请输入楼栋数" min="1" />
          </div>
          <div class="form-group">
            <label>当前天气</label>
            <select v-model.number="form.weatherTypeId" class="form-control">
              <option v-for="wt in weatherTypeList" :key="wt.id" :value="wt.id">
                {{ wt.name }}
              </option>
            </select>
          </div>
          <div class="form-group">
            <label>状态</label>
            <select v-model.number="form.status" class="form-control">
              <option :value="1">启用</option>
              <option :value="0">禁用</option>
            </select>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeModal" class="btn-cancel">取消</button>
          <button @click="saveCommunity" class="btn-save">保存</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { createCommunity, updateCommunity, getCommunityList, getWeatherTypeOptions } from '@/api/communities'
import { getProvinces, getCities, getDistricts } from '@/api/regions'

export default {
  name: 'Communities',
  data() {
    return {
      searchKey: '',
      currentPage: 1,
      totalPages: 10,
      showAddModal: false,
      editingCommunity: null,
      form: {
        name: '',
        provinceId: '',
        cityId: '',
        districtId: '',
        address: '',
        buildingCount: 0,
        weatherTypeId: 1,
        status: 1
      },
      communities: [],
      provinceList: [],
      cityList: [],
      districtList: [],
      weatherTypeList: [],
      weatherTypeMap: {},
      cityMap: {},
      districtMap: {}
    }
  },
  mounted() {
    this.loadProvinces()
    this.loadWeatherTypes()
    this.loadCommunityList()
  },
  methods: {
    async loadProvinces() {
      try {
        const response = await getProvinces()
        if (response.code === 200) {
          this.provinceList = response.data || []
        }
      } catch (error) {
      }
    },
    getProvinceName(provinceId) {
      const province = this.provinceList.find(p => p.id === provinceId)
      return province ? province.name : ''
    },
    async loadWeatherTypes() {
      try {
        const response = await getWeatherTypeOptions()
        if (response.code === 0) {
          this.weatherTypeList = response.data.weather_types || response.data || []
          this.weatherTypeList.forEach(wt => {
            this.weatherTypeMap[wt.id] = wt.name
          })
        }
      } catch (error) {
      }
    },
    getWeatherTypeName(id) {
      return this.weatherTypeMap[id] || '未知'
    },
    async getCityName(provinceId, cityId) {
      if (!cityId) return ''
      const cacheKey = `${provinceId}-${cityId}`
      if (this.cityMap[cacheKey]) return this.cityMap[cacheKey]
      try {
        const response = await getCities(provinceId)
        if (response.code === 200 && response.data && response.data.length > 0) {
          const city = response.data.find(c => c.id === cityId)
          if (city) {
            this.cityMap[cacheKey] = city.name
            return city.name
          }
        }
      } catch (error) {
      }
      return ''
    },
    async handleProvinceChange() {
      if (this.form.provinceId) {
        try {
          const response = await getCities(this.form.provinceId)
          if (response.code === 200) {
            this.cityList = response.data || []
          }
        } catch (error) {
          this.cityList = []
        }
      } else {
        this.cityList = []
        this.form.cityId = ''
      }
      this.districtList = []
      this.form.districtId = ''
    },
    async handleCityChange() {
      if (this.form.cityId) {
        try {
          const response = await getDistricts(this.form.cityId)
          if (response.code === 200) {
            this.districtList = response.data || []
          }
        } catch (error) {
          this.districtList = []
        }
      } else {
        this.districtList = []
        this.form.districtId = ''
      }
    },
    async loadCommunityList() {
      try {
        const params = { 
          page: this.currentPage, 
          pageSize: 20 
        }
        if (this.searchKey.trim()) {
          params.keyword = this.searchKey.trim()
        }
        const response = await getCommunityList(params)
        if (response.code === 0) {
          const data = response.data.list || response.data || []
          await this.fillRegionNames(data)
          this.communities = data
          this.totalPages = response.data.totalPages || 1
        }
      } catch (error) {
      }
    },
    async fillRegionNames(communities) {
      for (const community of communities) {
        const provinceId = community.provinceId || community.province_id || community.provinceCode
        const cityId = community.cityId || community.city_id || community.cityCode
        const districtId = community.districtId || community.district_id || community.districtCode
        
        community.provinceName = this.getProvinceName(provinceId)
        
        if (provinceId && cityId) {
          community.cityName = await this.getCityName(provinceId, cityId)
        }
        
        if (cityId && districtId) {
          community.districtName = await this.getDistrictName(cityId, districtId)
        }
      }
    },
    async getDistrictName(cityId, districtId) {
      if (!districtId) return ''
      const cacheKey = `${cityId}-${districtId}`
      if (this.districtMap[cacheKey]) return this.districtMap[cacheKey]
      try {
        const response = await getDistricts(cityId)
        if (response.code === 200 && response.data) {
          const district = response.data.find(d => d.id === districtId)
          if (district) {
            this.districtMap[cacheKey] = district.name
            return district.name
          }
        }
      } catch (error) {
      }
      return ''
    },
    search() {
      this.currentPage = 1
      this.loadCommunityList()
    },
    async editCommunity(community) {
      this.editingCommunity = community
      this.form.name = community.name
      this.form.provinceId = community.provinceId || community.province_id || community.provinceCode || ''
      this.form.cityId = community.cityId || community.city_id || community.cityCode || ''
      this.form.districtId = community.districtId || community.district_id || community.districtCode || ''
      this.form.address = community.address
      this.form.buildingCount = community.building_count || community.buildingCount || 0
      this.form.weatherTypeId = community.weatherTypeId || community.weather_type_id || 1
      this.form.status = community.status
      
      if (this.form.provinceId) {
        try {
          const cityResponse = await getCities(this.form.provinceId)
          if (cityResponse.code === 200) {
            this.cityList = cityResponse.data || []
          }
          if (this.form.cityId) {
            const districtResponse = await getDistricts(this.form.cityId)
            if (districtResponse.code === 200) {
              this.districtList = districtResponse.data || []
            }
          }
        } catch (error) {
        }
      }
      this.showAddModal = true
    },
    viewDetail(community) {
      this.$router.push(`/communities/detail/${community.id}`)
    },
    viewBuildings(community) {
      this.$router.push(`/communities/${community.id}`)
    },
    toggleStatus(community) {
      const action = community.status === 1 ? '禁用' : '启用'
      if (confirm(`确定要${action}小区 ${community.name} 吗？`)) {
        community.status = community.status === 1 ? 0 : 1
      }
    },
    closeModal() {
      this.showAddModal = false
      this.editingCommunity = null
      this.form = { name: '', provinceId: '', cityId: '', districtId: '', address: '', buildingCount: 0, weatherTypeId: 1, status: 1 }
      this.cityList = []
      this.districtList = []
    },
    async saveCommunity() {
      if (!this.form.name.trim()) {
        alert('请输入小区名称')
        return
      }
      if (!this.form.provinceId) {
        alert('请选择省份')
        return
      }
      if (!this.form.cityId) {
        alert('请选择城市')
        return
      }
      if (!this.form.districtId) {
        alert('请选择区县')
        return
      }
      if (!this.form.address.trim()) {
        alert('请输入详细地址')
        return
      }

      try {
        let response
        if (this.editingCommunity) {
          response = await updateCommunity(this.editingCommunity.id, this.form)
        } else {
          response = await createCommunity(this.form)
        }
        if (response && response.code === 0) {
          alert(this.editingCommunity ? '更新成功' : '添加成功')
          this.loadCommunityList()
          this.closeModal()
        } else {
          alert(response?.message || '操作失败')
        }
      } catch (error) {
        alert('保存失败，请稍后重试')
      }
    }
  }
}
</script>

<style scoped>
.communities-page {
  padding: 20px;
}

.filter-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 15px 20px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
}

.search-box {
  display: flex;
  gap: 10px;
}

.search-input {
  padding: 10px 15px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  width: 250px;
}

.search-btn {
  padding: 10px 20px;
  background: #3498db;
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.add-btn {
  padding: 10px 20px;
  background: #2ecc71;
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.table-container {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  overflow: hidden;
}

.communities-table {
  width: 100%;
  border-collapse: collapse;
}

.communities-table th, .communities-table td {
  padding: 14px 16px;
  text-align: left;
  border-bottom: 1px solid #eee;
  font-size: 14px;
}

.communities-table th {
  background: #f8f9fa;
  font-weight: 600;
  color: #333;
}

.status {
  border-radius: 20px;
  font-size: 12px;
}

.status-1 { background: #e8f5e9; color: #2ecc71; }
.status-0 { background: #ffebee; color: #e74c3c; }

.weather-type-tag {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  background: #fff3cd;
  color: #856404;
  display: inline-block;
}

.action-btn {
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  margin-right: 5px;
}

.action-btn.view {
  background: #1abc9c;
  color: #fff;
}

.action-btn.buildings {
  background: #9b59b6;
  color: #fff;
}

.action-btn.edit {
  background: #3498db;
  color: #fff;
}

.action-btn.disable {
  background: #e74c3c;
  color: #fff;
}

.action-btn.enable {
  background: #2ecc71;
  color: #fff;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  margin-top: 20px;
}

.page-btn {
  padding: 8px 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  font-size: 14px;
  color: #666;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-content {
  background: #fff;
  border-radius: 8px;
  width: 480px;
  max-width: 90%;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #eee;
}

.modal-header h3 {
  margin: 0;
  font-size: 16px;
}

.close-btn {
  font-size: 24px;
  background: none;
  border: none;
  cursor: pointer;
  color: #999;
}

.modal-body {
  padding: 20px;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  color: #333;
}

.form-control {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
}

.region-select {
  display: flex;
  gap: 8px;
}

.region-select-item {
  flex: 1;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  background: #fff;
  cursor: pointer;
}

.region-select-item:disabled {
  background: #f5f5f5;
  cursor: not-allowed;
  color: #999;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 15px 20px;
  border-top: 1px solid #eee;
}

.btn-cancel {
  padding: 8px 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
}

.btn-save {
  padding: 8px 16px;
  background: #3498db;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}
</style>
